/* eslint-disable */
import { ChevronLeft } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Label } from '../../Components/ui/label'
import { Input2 } from '../../Components/ui/input2'
import { Button } from '../../Components/ui/button'
import { Card, CardContent } from '../../Components/ui/card'
import { toCurrency } from '../../utils/CurrencyUtil'
import { useModal } from '../../hooks/use-modal-store'
import env from '../../env/env'
import { post } from '../../http/restservice'
import { useRecoilState } from 'recoil'
import { formatIndianNumber, sanitizeName } from '../../utils/Util'
import { userDetailsState } from '../LoginState'
import { useErrorPopup } from '../../hooks/useErrorPopup'
import PostHogTracker from '../../utils/PostHogTracker'
import OrderOtpVerification from '../FirstIdeas/OrderOtpVerification'
import { cn } from '../../lib/utils'

const PERCENTAGE_OPTIONS = [10, 25, 50, 75, 100]

const MFRedeem = () => {
  const location = useLocation()
  const [fundDetails] = useState(location?.state?.fundDetails)
  const [fund] = useState(location?.state?.fund)

  const [userDetails] = useRecoilState(userDetailsState)
  const email = userDetails?.email

  const navigate = useNavigate()
  const { onOpen } = useModal()
  const [redemptionPercentage, setRedemptionPercentage] = useState(100)
  const [redemptionAmount, setRedemptionAmount] = useState(fund?.holding || 0)
  const [redemptionAmountDisplay, setRedemptionAmountDisplay] = useState(
    fund?.holding ? toCurrency(Math.floor(fund.holding)) || '0' : '0',
  )
  const [redemptionQty, setRedemptionQty] = useState(fund?.balance)
  const [fullSell, setFullSell] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [openOTPPopup, setOpenOTPPopup] = useState(false)
  const [error, setError] = useState('')

  const clientCode = userDetails ? userDetails?.uid?.split('-')[0] : ''
  const { showError } = useErrorPopup()

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })

    if (fundDetails) {
      PostHogTracker.redeemButtonClicked(fundDetails.schemeName, fund?.holding)
      PostHogTracker.pageViewed('Redeem Fund', {
        fund_name: fundDetails.schemeName,
        current_value: fund?.holding,
      })
    }
  }, [])

  const handleRedemptionAmountChange = (e) => {
    setError('')
    const value = e.target.value.replace(/[^0-9]/g, '')
    const numericValue = Number(value) || 0

    setRedemptionAmountDisplay(toCurrency(numericValue) || '0')

    if (!isNaN(numericValue) && numericValue >= 0) {
      setRedemptionAmount(numericValue)
      setFullSell(false)
      setRedemptionPercentage(0)

      if (numericValue > Number(fund?.holding)) {
        setError('Entered amount exceeds available balance')
      }

      if (numericValue > 0 && value.length > 2) {
        PostHogTracker.redemptionAmountEntered(numericValue, fundDetails?.schemeName)
        if (numericValue < fund?.holding) {
          PostHogTracker.partialRedemptionSelected(fundDetails?.schemeName, numericValue)
          PostHogTracker.redemptionTypeSelected('Partial')
        }
      }
    }
  }

  const handleRedemptionPercentage = (btn) => {
    setError('')
    setRedemptionPercentage(btn)

    const amount = Number(fund?.holding) || 0
    const calculatedAmount = (amount * btn) / 100
    const isFull = btn === 100

    setRedemptionAmount(calculatedAmount)
    setRedemptionAmountDisplay(toCurrency(Math.floor(calculatedAmount)) || '0')
    setRedemptionQty(isFull ? fund?.balance : ((fund?.balance / 100) * btn).toFixed(4))
    setFullSell(isFull)

    if (isFull) {
      PostHogTracker.fullRedemptionSelected(fundDetails?.schemeName, fund?.balance)
      PostHogTracker.redemptionTypeSelected('Full')
    } else {
      PostHogTracker.partialRedemptionSelected(fundDetails?.schemeName, calculatedAmount)
      PostHogTracker.redemptionTypeSelected('Partial')
    }
  }

  const redeemOrder = async () => {
    PostHogTracker.redemptionOrderPlaced(
      fundDetails?.schemeName,
      redemptionAmount,
      redemptionQty,
      fullSell ? 'Full' : 'Partial',
    )

    try {
      setIsLoading(true)

      const payload = {
        clientCode: clientCode?.toUpperCase(),
        schemeCode: fundDetails?.bseSchemeCode,
        folioNumber: fund?.folioNo,
        withdrawableAmount: redemptionAmount,
        isAllRedeem: fullSell ? 'Y' : 'N',
        phoneNumber: formatIndianNumber(userDetails?.phoneNumber),
        isin: fundDetails?.isin,
      }

      const response = await post('mutualfundservice/api/bseStar/mfOrder/redeemOrder', payload)

      setIsLoading(false)

      if (!response.isError && response?.status === 'SUCCESS') {
        PostHogTracker.redemptionSuccess(fundDetails?.schemeName, redemptionAmount)
        onOpen('openPopup', {
          title: 'Redemption Order Processed',
          message:
            'Your redemption order is being processed and the amount will be credited to your bank account shortly.',
          severity: 'info',
          redirectBackTo:
            location?.state?.redirectBackTo || '/dashboard/mutual-funds/investments',
          redirectState: location?.state?.redirectState || { tab: 'Mutual Funds' },
          forwardTo: '/dashboard/mutual-funds',
        })
      }

      if (response.isError) {
        showError(
          'Redemption Failed',
          'There was an issue processing your redemption order. Please try again later or contact support for assistance.',
          () => { },
        )
      }
    } catch (error) {
      console.log('🚀 ~ redeemOrder ~ error:', error)
      setIsLoading(false)
    }
  }

  const generateOTP = async () => {
    setError('')

    if (Number(redemptionAmount) > Number(fund?.holding)) {
      return setError('Entered amount exceeds available balance')
    }

    setIsLoading(true)

    try {
      const response = await post(
        `${env.API_URL}mutualfundservice/api/mutualFund/sendTransactionalOtp`,
        {
          phoneNumber: userDetails?.phoneNumber,
          email: email?.includes('fabits') ? '' : email,
        },
      )

      if (response.isError) {
        PostHogTracker.buttonClicked('Order OTP Resend Failed', {
          action: 'Order OTP Resend Failed',
          page_name: 'Redeem Fund Page',
        })
        showError('OTP Error', 'Failed to generate OTP. Please try again later.', () => { })
        return
      }

      PostHogTracker.buttonClicked('Order OTP Resend Success', {
        action: 'Order OTP Resend Successful',
        page_name: 'Redeem Fund Page',
      })
      setOpenOTPPopup(true)
    } catch (error) {
      console.log('🚀 ~ generateOTP ~ error:', error)
      showError('OTP Error', 'Failed to generate OTP. Please try again later.', () => { })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <div className='bg-white fabits-order-page'>
        <div className='max-w-[1128px] mx-auto min-h-screen pb-12 fabits-order-shell'>
          <div
            className='flex gap-x-1 items-center font-semibold cursor-pointer lg:py-2.5 p-4 lg:px-0 fabits-order-header'
            onClick={() => navigate(-1)}
          >
            <ChevronLeft className='size-5' />
            Redeem
          </div>

          <div className='max-w-[360px] mx-auto h-full flex flex-col gap-y-8 fabits-order-panel'>
            <div className='min-h-[170px] w-full flex justify-center items-center p-4 z-0 relative fabits-order-ticket'>
              <div className='flex flex-col items-center justify-center text-center z-10 fabits-order-ticket-content'>
                <img
                  src={`${env.MF_LOGOS_URL}/${fundDetails?.amcCode}.png`}
                  alt={fundDetails?.legalName}
                  className='size-12 border rounded-lg fabits-order-fund-logo'
                />
                <h3 className='font-semibold text-sm leading-tight mt-3 text-pretty fabits-order-fund-name'>
                  {sanitizeName(fundDetails)}
                </h3>
                <div className='flex flex-col items-center justify-center mt-3'>
                  <span className='text-xs fabits-order-label'>Available Balance</span>
                  <span className='font-semibold text-2xl fabits-amount'>
                    ₹ {toCurrency(Math.floor(fund?.holding || 0))}
                  </span>
                </div>
              </div>
            </div>

            <div className='flex flex-col items-center gap-1.5 fabits-order-amount-block'>
              <Label htmlFor='amount' className='text-black-400 text-xs fabits-order-label'>
                Withdrawal Amount
              </Label>
              <Input2
                startAdornment='₹'
                type='text'
                id='amount'
                placeholder='Amount'
                className='border-none outline-none font-DMSans font-semibold text-3xl w-auto min-w-[100px] max-w-[360px] fabits-order-amount-input fabits-amount'
                style={{
                  width: `${Math.max(String(redemptionAmountDisplay).length * 24 + 80, 120)}px`,
                }}
                value={redemptionAmountDisplay}
                onChange={handleRedemptionAmountChange}
              />
              {error && <p className='text-xs text-error-600 text-center'>{error}</p>}
            </div>

            <div className='flex flex-wrap justify-center gap-2'>
              {PERCENTAGE_OPTIONS.map((btn) => (
                <Button
                  key={btn}
                  variant='simple'
                  size='simple'
                  className={cn(
                    'px-4 text-sm',
                    btn === redemptionPercentage
                      ? 'fabits-range-pill fabits-range-pill-active'
                      : 'fabits-range-pill',
                  )}
                  onClick={() => handleRedemptionPercentage(btn)}
                >
                  {btn === 100 ? 'Full' : `${btn}%`}
                </Button>
              ))}
            </div>

            <Card className='w-full fabits-order-info-card'>
              <CardContent className='p-4'>
                <h5 className='font-medium'>Important Notice</h5>
                <p className='text-sm text-black-300 mt-1'>
                  Withdrawn funds will be transferred to your linked bank account within 1-5
                  business days.
                </p>
              </CardContent>
            </Card>

            <Button
              className='w-full rounded-xl bg-primary-500 fabits-order-primary-action'
              loading={isLoading}
              onClick={generateOTP}
              disabled={
                isLoading ||
                Number(redemptionAmount) > Number(fund?.holding) ||
                Number(redemptionAmount) <= 0
              }
            >
              Continue Withdrawal
            </Button>
          </div>
        </div>
      </div>

      <OrderOtpVerification
        phoneNumber={userDetails?.phoneNumber}
        openOTPPopup={openOTPPopup}
        setOpenOTPPopup={setOpenOTPPopup}
        email={email}
        handleSipOrOT={redeemOrder}
      />
    </>
  )
}

export default MFRedeem
